import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHelmet from "@/components/base/SEOHelmet";
import { videos, heroVideos, categories } from "@/mocks/videos";
import { articles } from "@/mocks/articles";
import { faqItems } from "@/mocks/about";

const hotVideos = videos.filter((v) => v.isHot).slice(0, 6);
const newVideos = videos.filter((v) => v.isNew).slice(0, 6);
const hotArticles = articles.filter((a) => a.isHot).slice(0, 4);
const displayCategories = categories.filter((c) => c.slug !== "all").slice(0, 8);

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "91看网页版",
  url: "https://www.91kan.com/",
  description: "91看网页版是您最佳的在线影视观看选择，无需下载安装，直接通过浏览器即可畅享海量高清视频内容。",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://www.91kan.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <SEOHelmet
        title="91看网页版 - 官方正版在线观看平台 | 首页"
        description="91看网页版是您最佳的在线影视观看选择，无需下载安装，直接通过浏览器即可畅享海量高清视频内容。支持多终端访问，界面简洁流畅，提供极致观影体验。"
        keywords="91看网页版,91看网页版在线观看,91看网页版入口,91看网页版最新地址,91看网页版高清播放,91看官网网页版"
        canonicalUrl="https://www.91kan.com/"
        ogImage={heroVideos[0].poster}
        schemaData={homeSchema}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
          <img
            src={heroVideos[0].poster}
            alt="91看网页版 - 在线高清影视平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="91看网页版在线观看 - 高清影视尽在掌握"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-8">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/90 text-background-50 text-xs font-medium mb-5">
                  <i className="ri-fire-line"></i>
                  <span>正在热播</span>
                </div>
                <h1
                  className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  91看网页版 - 官方正版在线观看平台
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl leading-relaxed">
                  无需下载安装，打开浏览器即可畅享海量高清视频内容。支持多终端访问，界面简洁流畅，为您提供极致的网页在线观看体验。
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/category/all"
                    className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 text-center"
                  >
                    <i className="ri-play-circle-line mr-2"></i>立即观看
                  </Link>
                  <Link
                    to="/about"
                    className="px-6 py-3 rounded-full border border-white/30 text-white font-medium text-sm whitespace-nowrap hover:bg-white/10 transition-colors duration-200 text-center"
                  >
                    <i className="ri-information-line mr-2"></i>了解更多
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-6 left-4 md:left-8 flex items-center gap-2">
            <div className="flex items-center gap-1">
              <i className="ri-star-fill text-yellow-400 text-sm"></i>
              <span className="text-white/90 text-sm font-medium">{heroVideos[0].rating}</span>
            </div>
            <span className="text-white/40 text-sm">|</span>
            <span className="text-white/70 text-sm">{heroVideos[0].year}</span>
            <span className="text-white/40 text-sm">|</span>
            <span className="text-white/70 text-sm">{heroVideos[0].duration}</span>
          </div>
        </section>

        {/* Hot Videos Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-2">精选高分影视，不容错过</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
            >
              查看全部 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
            {hotVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                  <img
                    src={video.poster}
                    alt={video.title}
                    title={`${video.title} - 91看网页版在线观看`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/60 text-yellow-400 text-xs">
                    <i className="ri-star-fill text-[10px]"></i>
                    {video.rating}
                  </div>
                  {video.isVip && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-[10px] font-medium">
                      VIP
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex flex-wrap gap-1">
                      {video.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px]">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                  {video.title}
                </h3>
                <p className="text-xs text-foreground-400 mt-1">
                  {video.year} · {video.duration}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                分类浏览
              </h2>
              <p className="text-sm text-foreground-500 mt-2">按类型探索你感兴趣的内容</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
              {displayCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-5 rounded-xl bg-background-50 hover:bg-primary-50 transition-colors duration-200 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors duration-200">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* New Videos Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                最新上线
              </h2>
              <p className="text-sm text-foreground-500 mt-2">刚刚更新的热门内容</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
            >
              查看全部 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
            {newVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                  <img
                    src={video.poster}
                    alt={video.title}
                    title={`${video.title} - 91看网页版在线观看`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/60 text-yellow-400 text-xs">
                    <i className="ri-star-fill text-[10px]"></i>
                    {video.rating}
                  </div>
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-[10px] font-medium">
                    NEW
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                  {video.title}
                </h3>
                <p className="text-xs text-foreground-400 mt-1">
                  {video.year} · {video.duration}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Latest Articles */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  影视资讯
                </h2>
                <p className="text-sm text-foreground-500 mt-2">最新影视动态与深度影评</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                更多资讯 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {hotArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-xl bg-background-50 overflow-hidden hover:bg-background-50 transition-colors duration-200"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.cover}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200">
                      <span className="text-xs text-foreground-400">{article.author}</span>
                      <span className="text-xs text-foreground-300">{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 mt-2">关于91看网页版，你可能想了解这些</p>
          </div>
          <div className="space-y-3">
            {faqItems.slice(0, 5).map((faq, idx) => (
              <details key={idx} className="group rounded-xl bg-background-50 border border-background-200/70">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-lg flex-shrink-0"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/about"
              className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              查看更多问题 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-foreground-950"></div>
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20cinematic%20texture%20with%20soft%20bokeh%20lights%20and%20film%20grain%2C%20dark%20elegant%20background%2C%20subtle%20golden%20particles&width=1600&height=400&seq=cta-bg-pattern&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative max-w-3xl mx-auto text-center px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              即刻开始您的在线观影之旅
            </h2>
            <p className="text-sm md:text-base text-background-50/60 mb-8">
              无需注册，无需下载，打开浏览器即可畅享数千部高清影视内容
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-8 py-3.5 rounded-full bg-accent-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-accent-600 transition-colors duration-200"
            >
              <i className="ri-play-circle-line mr-2 text-lg"></i>
              立即观看
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}